import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { AppConfigContext } from "../../../context";
import { AssetConfig } from "../../../config/assets";
import { useBalance } from "../../../hooks/useBalance";
import { useMintTo } from "../../../hooks/useMintTo";
import { useExchangeRate } from "../../../hooks/useExchangeRate";
import { useConvert } from "../../../hooks/useConvert";
import { useJitoSOLRate } from "../../../hooks/useJitoSOLRate";
import { useNativeBalance } from "../../../hooks/useNativeBalance";
import { useLstBalances } from "../../../hooks/useLstBalances";
import { useTokensDeposited } from "../../../hooks/useTokensDeposited";
import { useErrorToast, useSuccessToast } from "../../../helpers/toast";
import { solAmountFormatter } from "../../../helpers/formatter";
import { parseNumberString } from "../../../utils/format";
import {
  fetchJupiterBuild,
  getJupiterPriceImpactLabel,
  JupiterBuildResponse,
} from "../../../helpers/jupiterApi";
import { RestakeSolTokenPickerProps } from "../RestakeSolTokenPicker";
import { StakeSlippageSelector } from "../StakeSlippageSelector";
import { StakeFormProps, TokenAddon } from "../StakeForm";

interface UseRestakeSolOptions {
  config: AssetConfig;
  resetKey: string;
}

export const useRestakeSol = ({ config, resetKey }: UseRestakeSolOptions) => {
  const { publicKey } = useWallet();
  const { connection } = useConnection();
  const {
    solVrtMintAddress,
    jitoSolMintAddress,
    solVaultMaxCap,
    solVaultPaused,
    solVaultAddress,
    apiUrl,
  } = useContext(AppConfigContext);

  const slippageOptions = [0.1, 0.3, 1];

  const [selectedToken, setSelectedToken] = useState("JitoSOL");
  const [selectedTokenImage, setSelectedTokenImage] = useState("/jitosol.png");
  const [selectedTokenMint, setSelectedTokenMint] = useState(jitoSolMintAddress);
  const [displayedBalance, setDisplayedBalance] = useState("0 JitoSOL");
  const [formDisabled, setFormDisabled] = useState(false);
  const [capReached, setCapReached] = useState(false);
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [priceImpact, setPriceImpact] = useState("0% Slippage");
  const [quoteResponse, setQuoteResponse] = useState<JupiterBuildResponse | null>(null);
  const [slippageBps, setSlippageBps] = useState(0.3);
  const [inputChanged, setInputChanged] = useState<string | null>(null);
  const [inputTimer, setInputTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [shouldFetch, setShouldFetch] = useState(false);

  const { balance: tokenBalance, fetchBalance: fetchTokenBalance } = useBalance();
  const { balance: solBalance, fetchNativeBalance } = useNativeBalance();
  const { fetchLstAvailable, fetchLstBalances, lstBalances, isLoading: lstLoading } =
    useLstBalances();
  const { errorToast } = useErrorToast();
  const { successToast } = useSuccessToast();
  const { mintTo, error: mintToError } = useMintTo();
  const { rate: kySolRate, fetchRate: fetchKySolRate } = useExchangeRate();
  const { tokensDeposited, fetchTokensDeposited } = useTokensDeposited();
  const { convert, error: convertError } = useConvert();

  const estimatedKySolRate = 1;
  const estimatedJitoSolRate = 1.148;
  const { rate: jitoSolRate, fetchRate } = useJitoSOLRate();

  const isRestake = selectedToken === "SOL" || selectedToken === "JitoSOL";

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (!/^\d*\.?\d*$/.test(inputValue) && inputValue !== "") {
      return;
    }
    if (inputTimer) {
      clearTimeout(inputTimer);
    }
    setShouldFetch(false);
    const lastChar = inputValue.slice(-1);
    if (lastChar === "," || lastChar === "." || lastChar === "'" || inputValue.includes(".")) {
      setInputChanged(inputValue);
    } else {
      const number = parseNumberString(inputValue);
      const formattedInput = number.toLocaleString("en-US", { maximumFractionDigits: 5 });
      setInputChanged(formattedInput);
    }
    const newTimer = setTimeout(() => {
      setShouldFetch(true);
    }, 1000);
    setInputTimer(newTimer);
  };

  const handleMaxClick = () => {
    const remainingCap = solVaultMaxCap - (Number(tokensDeposited) || 0);
    let balance = Number(tokenBalance);
    if (selectedToken === "SOL") {
      balance = Number(solBalance);
    }
    const maxAmount = solAmountFormatter.format(Math.min(balance, remainingCap));
    setInputChanged(maxAmount.replace(/,/g, ""));
    setShouldFetch(true);
  };

  const formik = useFormik({
    initialValues: { amount: "", receivedAmount: "" },
    onSubmit: async (values) => {
      try {
        if (isRestake) {
          const signature = await mintTo(
            solVrtMintAddress,
            jitoSolMintAddress,
            solVaultAddress,
            values.amount,
            selectedToken === "SOL"
          );
          if (signature) {
            successToast("Your mint was successful!");
            reload();
          }
        } else if (quoteResponse) {
          const signature = await convert(quoteResponse);
          if (signature) {
            successToast("Your conversion was successful!");
            reload();
          }
        }
      } catch (err) {
        errorToast(`${err}`);
      }
    },
  });

  const reload = () => {
    formik.resetForm();
    setInputChanged(null);
    fetchNativeBalance(publicKey);
    fetchTokenBalance(new PublicKey(selectedTokenMint), publicKey);
    fetchRate(connection);
    fetchTokensDeposited();
    fetchKySolRate(solVaultAddress);
    fetchLstAvailable(publicKey);
    fetchLstBalances(publicKey);
  };

  useEffect(() => {
    if (mintToError) {
      errorToast(mintToError);
      formik.setSubmitting(false);
    }
    if (convertError) {
      errorToast(convertError);
      formik.setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mintToError, convertError]);

  useEffect(() => {
    setSelectedToken("JitoSOL");
    setSelectedTokenImage("/jitosol.png");
    setSelectedTokenMint(jitoSolMintAddress);
    setInputChanged(null);
    formik.resetForm();
    fetchRate(connection);
    fetchNativeBalance(publicKey);
    fetchTokenBalance(new PublicKey(jitoSolMintAddress), publicKey);
    fetchLstAvailable(publicKey);
    fetchTokensDeposited();
    fetchKySolRate(solVaultAddress);
    fetchLstBalances(publicKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    const balanceCheck =
      selectedToken === "SOL"
        ? parseNumberString(formik.values.amount) > Number(solBalance)
        : parseNumberString(formik.values.amount) > Number(tokenBalance);
    const supply = Number(tokensDeposited);
    const receivedAmount = Number(formik.values.receivedAmount.replace(/[^0-9.]/g, ""));
    const capCheck = supply > solVaultMaxCap || supply + receivedAmount > solVaultMaxCap;
    const biggerThanZeroCheck = parseNumberString(formik.values.amount) > 0;
    setFormDisabled(balanceCheck || capCheck || !biggerThanZeroCheck);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.amount, tokenBalance, solBalance, selectedToken, tokensDeposited]);

  useEffect(() => {
    if (tokensDeposited) {
      setCapReached(Number(tokensDeposited) >= solVaultMaxCap * 0.99999);
    }
  }, [tokensDeposited, solVaultMaxCap]);

  useEffect(() => {
    if (selectedToken === "SOL") {
      const receivedValue = inputChanged
        ? `≈ ${solAmountFormatter.format(
            parseNumberString(inputChanged) *
              (jitoSolRate ?? estimatedJitoSolRate) *
              (kySolRate ?? estimatedKySolRate)
          )}`
        : "";
      formik.setValues({ amount: inputChanged ?? "", receivedAmount: receivedValue });
      fetchNativeBalance(publicKey);
      setSelectedTokenImage("/solana.png");
      setExchangeRate((jitoSolRate ?? estimatedJitoSolRate) * (kySolRate ?? estimatedKySolRate));
      setPriceImpact("No slippage");
    } else if (selectedToken === "JitoSOL") {
      const receivedValue = inputChanged
        ? `${solAmountFormatter.format(
            parseNumberString(inputChanged) * (kySolRate ?? estimatedKySolRate)
          )}`
        : "";
      formik.setValues({ amount: inputChanged ?? "", receivedAmount: receivedValue });
      fetchTokenBalance(new PublicKey(jitoSolMintAddress), publicKey);
      setSelectedTokenImage("/jitosol.png");
      setSelectedTokenMint(jitoSolMintAddress);
      setExchangeRate(kySolRate ?? estimatedKySolRate);
      setPriceImpact("No slippage");
    } else {
      const fetchJupiterRate = async () => {
        const selectedLst = lstBalances?.find(
          (lst) => lst.metadata?.symbol.toLowerCase() === selectedToken.toLowerCase()
        );
        if (!selectedToken || !selectedLst || selectedToken === "SOL" || selectedToken === "JitoSOL") {
          return;
        }
        if (!publicKey) {
          return;
        }
        if (!apiUrl) {
          errorToast("API URL is not configured.");
          return;
        }
        const amount =
          inputChanged && parseNumberString(inputChanged) > 0
            ? parseNumberString(inputChanged) * 1e9
            : 10 * 1e9;
        if (!shouldFetch && inputChanged !== null) {
          formik.setValues({ amount: inputChanged ?? "", receivedAmount: "🔄" });
          return;
        }
        try {
          const response = await fetchJupiterBuild(apiUrl, {
            inputMint: selectedLst.metadata?.mint ?? "",
            outputMint: solVrtMintAddress,
            amount: amount.toFixed(0),
            taker: publicKey.toBase58(),
            slippageBps: Math.round(slippageBps * 100),
          });
          const inAmount = Number(response.inAmount);
          const outAmount = Number(response.outAmount);
          const rate = inAmount > 0 ? outAmount / inAmount : 0;
          const newReceivedAmount = inputChanged
            ? `≈ ${(parseNumberString(inputChanged) * rate).toFixed(9)}`
            : "";
          formik.setValues({ amount: inputChanged ?? "", receivedAmount: newReceivedAmount });
          fetchTokenBalance(new PublicKey(selectedLst.metadata?.mint ?? ""), publicKey);
          setSelectedTokenImage(selectedLst.metadata?.imageUri ?? "/unknown.svg");
          setSelectedTokenMint(selectedLst.metadata?.mint ?? "");
          setExchangeRate(rate);
          setPriceImpact(getJupiterPriceImpactLabel(response));
          setQuoteResponse(response);
        } catch (err) {
          errorToast(`${err}`);
          setExchangeRate(0);
          setPriceImpact("No slippage");
          formik.setValues({ amount: inputChanged ?? "", receivedAmount: "" });
          setQuoteResponse(null);
        }
      };
      fetchJupiterRate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedToken, slippageBps, kySolRate, jitoSolRate, inputChanged, shouldFetch, publicKey, apiUrl]);

  useEffect(() => {
    if (selectedToken === "SOL") {
      setDisplayedBalance(solAmountFormatter.format(Number(solBalance)) + " SOL");
    } else {
      setDisplayedBalance(solAmountFormatter.format(Number(tokenBalance)) + " " + selectedToken);
    }
  }, [solBalance, tokenBalance, selectedToken]);

  useEffect(() => {
    return () => {
      if (inputTimer) {
        clearTimeout(inputTimer);
      }
    };
  }, [inputTimer]);

  const jitoSolBalance = useMemo(() => {
    const fromLst = lstBalances?.find((lst) => lst.metadata?.mint === jitoSolMintAddress);
    if (fromLst != null) return fromLst.balance;
    if (selectedToken === "JitoSOL") return Number(tokenBalance?.toString() ?? 0);
    return 0;
  }, [lstBalances, jitoSolMintAddress, selectedToken, tokenBalance]);

  const handleTokenPickerOpen = useCallback(() => {
    fetchNativeBalance(publicKey);
    fetchTokenBalance(new PublicKey(jitoSolMintAddress), publicKey);
    fetchLstBalances(publicKey);
  }, [publicKey, jitoSolMintAddress, fetchNativeBalance, fetchTokenBalance, fetchLstBalances]);

  const handleTokenSelect = useCallback((symbol: string, imageUri?: string) => {
    setSelectedToken(symbol);
    if (imageUri) {
      setSelectedTokenImage(imageUri);
    }
  }, []);

  const tokenPickerProps: RestakeSolTokenPickerProps = {
    selectedSymbol: selectedToken,
    selectedImage: selectedTokenImage,
    onSelect: handleTokenSelect,
    solBalance: Number(solBalance?.toString() ?? 0),
    jitoSolMint: jitoSolMintAddress,
    jitoSolBalance,
    lstBalances,
    lstLoading,
    onOpen: handleTokenPickerOpen,
  };

  const stakeFormProps: StakeFormProps = {
    displayedBalance,
    amount: formik.values.amount,
    receivedAmount: formik.values.receivedAmount,
    onAmountChange: handleInputChange,
    onMax: handleMaxClick,
    maxDisabled: capReached,
    outputTokenSelector: <TokenAddon image={config.vrtImage} symbol={config.vrtName} />,
    receiveHint: `${priceImpact} ${!isRestake ? "via Jupiter" : ""}`.trim(),
    submitLabel: capReached ? "Cap filled" : "Convert to kySOL",
    formDisabled,
    vaultPaused: solVaultPaused,
    isSubmitting: formik.isSubmitting,
    onSubmit: formik.handleSubmit,
    details: [
      ...(kySolRate
        ? [
            {
              label: "Rate",
              value: `1 ${selectedToken} = ${exchangeRate?.toFixed(5) ?? 1} kySOL`,
            },
          ]
        : []),
      ...(!isRestake
        ? [
            {
              label: "Slippage",
              valueNode: (
                <StakeSlippageSelector
                  showLabel={false}
                  options={slippageOptions}
                  value={slippageBps}
                  onChange={setSlippageBps}
                />
              ),
            },
          ]
        : []),
    ],
  };

  return { stakeFormProps, tokenPickerProps };
};
